import React from "react";

type StreamFadeProps = {
  children?: React.ReactNode;
  as: keyof React.JSX.IntrinsicElements;
  className?: string;
  streaming: boolean;
  animateInitial: boolean;
  [key: string]: unknown; // Additional HTML props to forward
};

type AnimatedRange = {
  start: number;
  end: number;
  key: string;
};

/**
 * Tracks text appended to one Markdown element and keeps every appended
 * chunk as a separate, stable React element.
 *
 * Unlike the previous implementation, the latest chunk is NOT used as the
 * only animation baseline. Every streamed update gets its own persistent
 * range/key, so several chunks can be animating at the same time.
 */
export class StreamFade extends React.Component<
  StreamFadeProps,
  {
    animatedRanges: AnimatedRange[];
  }
> {
  private _previousText: string;
  private _hasRendered = false;
  private _nextRangeId = 0;

  constructor(props: StreamFadeProps) {
    super(props);

    const text = StreamFade._textContent(props.children);

    this._previousText = props.animateInitial ? "" : text;

    this.state = {
      animatedRanges: [],
    };
  }

  componentDidUpdate(): void {
    const currentText = StreamFade._textContent(this.props.children);

    if (!this.props.streaming) {
      this._previousText = currentText;

      if (this.state.animatedRanges.length > 0) {
        this.setState({
          animatedRanges: [],
        });
      }

      this._hasRendered = true;
      return;
    }

    const previousText = this._previousText;

    /*
     * We only treat text as streaming when it is a true append.
     *
     * If Markdown reparses the content and the resulting text is no longer
     * prefixed by the previous text, there is no reliable way to identify
     * the new portion, so we reset the animation history.
     */
    if (!currentText.startsWith(previousText)) {
      this._previousText = currentText;

      if (this.state.animatedRanges.length > 0) {
        this.setState({
          animatedRanges: [],
        });
      }

      this._hasRendered = true;
      return;
    }

    /*
     * If text was appended, create a NEW immutable range.
     *
     * This happens in componentDidUpdate, i.e. after the new text has already
     * been committed. The following render creates a new <span> with a new
     * key. Existing spans remain untouched, so their CSS animations are not
     * restarted.
     */
    if (currentText.length > previousText.length) {
      const newRange: AnimatedRange = {
        start: previousText.length,
        end: currentText.length,
        key: `stream-fade-${this._nextRangeId++}`,
      };

      this.setState(state => ({
        animatedRanges: [...state.animatedRanges, newRange],
      }));
    }

    this._previousText = currentText;
    this._hasRendered = true;
  }

  private static _textContent(children: React.ReactNode): string {
    if (children == null || typeof children === "boolean") {
      return "";
    }

    if (typeof children === "string" || typeof children === "number") {
      return String(children);
    }

    if (Array.isArray(children)) {
      return children
        .map(child => StreamFade._textContent(child))
        .join("");
    }

    if (React.isValidElement(children)) {
      /*
       * Code/pre content is deliberately excluded because Markdown code
       * blocks have their own rendering semantics.
       */
      if (children.type === "code" || children.type === "pre") {
        return "";
      }

      return StreamFade._textContent(
        (children.props as { children?: React.ReactNode }).children,
      );
    }

    return "";
  }

  private static _isBlockElement(type: unknown): boolean {
    return (
      type === "p" ||
      type === "h1" ||
      type === "h2" ||
      type === "h3" ||
      type === "h4" ||
      type === "h5" ||
      type === "h6" ||
      type === "ul" ||
      type === "ol" ||
      type === "li" ||
      type === "table" ||
      type === "thead" ||
      type === "tbody" ||
      type === "tfoot" ||
      type === "tr" ||
      type === "td" ||
      type === "th" ||
      type === "blockquote" ||
      type === "div"
    );
  }

  /**
   * Wraps text portions belonging to the persisted animation ranges.
   *
   * `cursor.value` is the text offset within this StreamFade instance.
   */
  private _wrapAnimatedRanges(
    children: React.ReactNode,
    ranges: AnimatedRange[],
    cursor: { value: number },
  ): React.ReactNode {
    if (children == null || typeof children === "boolean") {
      return children;
    }

    if (typeof children === "string" || typeof children === "number") {
      const text = String(children);

      const nodeStart = cursor.value;
      const nodeEnd = nodeStart + text.length;

      cursor.value = nodeEnd;

      /*
       * Find all animation ranges intersecting this text node.
       */
      const intersectingRanges = ranges.filter(
        range => range.start < nodeEnd && range.end > nodeStart,
      );

      if (intersectingRanges.length === 0) {
        return children;
      }

      const parts: React.ReactNode[] = [];
      let position = 0;

      for (const range of intersectingRanges) {
        const localStart = Math.max(0, range.start - nodeStart);
        const localEnd = Math.min(text.length, range.end - nodeStart);

        if (localStart > position) {
          parts.push(text.slice(position, localStart));
        }

        if (localEnd > localStart) {
          parts.push(
            <span
              key={`${range.key}-${nodeStart}-${localStart}-${localEnd}`}
              className="stream-fade-in"
            >
              {text.slice(localStart, localEnd)}
            </span>,
          );
        }

        position = Math.max(position, localEnd);
      }

      if (position < text.length) {
        parts.push(text.slice(position));
      }

      return <>{parts}</>;
    }

    if (Array.isArray(children)) {
      return children.map((child, index) => (
        <React.Fragment key={index}>
          {this._wrapAnimatedRanges(child, ranges, cursor)}
        </React.Fragment>
      ));
    }

    if (!React.isValidElement(children)) {
      return children;
    }

    /*
     * Never recursively process code/pre.
     */
    if (children.type === "code" || children.type === "pre") {
      return children;
    }

    /*
     * Nested block elements own their own StreamFade instance.
     *
     * We count their text so offsets remain correct for the parent, but
     * don't wrap their content here.
     */
    if (StreamFade._isBlockElement(children.type)) {
      cursor.value += StreamFade._textContent(children).length;
      return children;
    }

    const childProps = children.props as {
      children?: React.ReactNode;
    };

    if (childProps.children === undefined) {
      return children;
    }

    return React.cloneElement(
      children,
      undefined,
      this._wrapAnimatedRanges(
        childProps.children,
        ranges,
        cursor,
      ),
    );
  }

  render(): React.ReactNode {
    const {
      as,
      className,
      children,
      streaming,
      animateInitial,
    } = this.props;

    const currentText = StreamFade._textContent(children);

    /*
     * Initial animation:
     *
     * The first render has no persisted range yet. We intentionally render
     * the entire initial text as animated. After commit, it becomes a normal
     * persisted range with a stable key.
     */
    const shouldAnimateInitial =
      streaming &&
      animateInitial &&
      !this._hasRendered &&
      currentText.length > 0;

    let renderedChildren: React.ReactNode;

    if (shouldAnimateInitial) {
      const initialRange: AnimatedRange = {
        start: 0,
        end: currentText.length,
        key: "stream-fade-initial",
      };

      renderedChildren = this._wrapAnimatedRanges(
        children,
        [initialRange],
        { value: 0 },
      );
    } else if (streaming && this.state.animatedRanges.length > 0) {
      renderedChildren = this._wrapAnimatedRanges(
        children,
        this.state.animatedRanges,
        { value: 0 },
      );
    } else {
      renderedChildren = children;
    }

    return React.createElement(
      as,
      {
        className,
      },
      renderedChildren,
    );
  }
}